import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Cell, Pie, PieChart, PieLabelRenderProps, ResponsiveContainer, Tooltip } from 'recharts';
import { API_BASE_URL } from '../../config/api';

interface WorkStatus {
  backlog: number;
  started: number;
  completed: number;
  canceled: number;
}

interface WorkCount {
  name: string;
  count: number;
}

interface WorkProject {
  name: string;
  state: string;
  progress: number;
  targetDate: string | null;
}

interface WorkCycle {
  name: string;
  startsAt: string | null;
  endsAt: string | null;
  progress: number;
}

interface WorkTeam {
  id: string;
  name: string;
  key: string | null;
  cycle: WorkCycle | null;
  status: WorkStatus;
  open: number;
  completedLast30Days: number;
  createdLast30Days: number;
  states: WorkCount[];
  priorities: WorkCount[];
  projects: WorkProject[];
}

interface WorkData {
  configured: boolean;
  error: string | null;
  organization: string | null;
  status: WorkStatus;
  open: number;
  completedLast30Days: number;
  createdLast30Days: number;
  states: WorkCount[];
  priorities: WorkCount[];
  projects: WorkProject[];
  teams: WorkTeam[];
}

const STATUS_ORDER: Array<keyof WorkStatus> = ['backlog', 'started', 'completed', 'canceled'];
const STATUS_LABELS: Record<keyof WorkStatus, string> = {
  backlog: 'Backlog',
  started: 'In progress',
  completed: 'Done',
  canceled: 'Canceled',
};
const STATUS_COLORS: Record<keyof WorkStatus, string> = {
  backlog: '#9ca3af',
  started: '#2563eb',
  completed: '#16a34a',
  canceled: '#dc2626',
};
const COUNT_COLORS = ['#2563eb', '#7c3aed', '#0d9488', '#ea580c', '#db2777', '#16a34a', '#f59e0b', '#0369a1'];

const renderLabel = (props: PieLabelRenderProps) => {
  const { cx, cy, midAngle, innerRadius, outerRadius, percent } = props;
  if (!percent || percent < 0.05 || [cx, cy, innerRadius, outerRadius, midAngle].some((value) => typeof value !== 'number')) {
    return null;
  }
  const radius = Number(innerRadius) + (Number(outerRadius) - Number(innerRadius)) * 0.6;
  const x = Number(cx) + radius * Math.cos((-Number(midAngle) * Math.PI) / 180);
  const y = Number(cy) + radius * Math.sin((-Number(midAngle) * Math.PI) / 180);
  return (
    <text x={x} y={y} fill="#fff" textAnchor={x > Number(cx) ? 'start' : 'end'} dominantBaseline="central" fontSize={12} fontWeight={700}>
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

const formatDate = (value?: string | null) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const CountList: React.FC<{ rows: WorkCount[] }> = ({ rows }) => {
  if (!rows.length) {
    return <p className="store-dash-hint">Nothing to show yet.</p>;
  }
  const total = rows.reduce((sum, row) => sum + row.count, 0);
  return (
    <div className="store-dash-legend">
      {rows.map((row, index) => (
        <div key={row.name} className="store-dash-legend-item">
          <i style={{ background: COUNT_COLORS[index % COUNT_COLORS.length] }} />
          <span>{row.name}</span>
          <em>
            {row.count.toLocaleString()}
            {total ? ` · ${Math.round((row.count / total) * 100)}%` : ''}
          </em>
        </div>
      ))}
    </div>
  );
};

const TeamDetail: React.FC<{
  title: string;
  status: WorkStatus;
  open: number;
  completedLast30Days: number;
  createdLast30Days: number;
  states: WorkCount[];
  priorities: WorkCount[];
  projects: WorkProject[];
  cycle?: WorkCycle | null;
}> = ({
  title,
  status,
  open,
  completedLast30Days,
  createdLast30Days,
  states,
  priorities,
  projects,
  cycle,
}) => {
  const slices = STATUS_ORDER.map((key) => ({
    name: key,
    displayName: STATUS_LABELS[key],
    value: status[key],
    color: STATUS_COLORS[key],
  }));
  const total = slices.reduce((sum, slice) => sum + slice.value, 0);

  return (
    <>
      <div className="store-dash-cards">
        <div className="store-dash-card">
          <span>Open</span>
          <strong>{open.toLocaleString()}</strong>
        </div>
        <div className="store-dash-card">
          <span>In progress</span>
          <strong>{status.started.toLocaleString()}</strong>
        </div>
        <div className="store-dash-card">
          <span>Done</span>
          <strong>{status.completed.toLocaleString()}</strong>
        </div>
        <div className="store-dash-card">
          <span>Canceled</span>
          <strong>{status.canceled.toLocaleString()}</strong>
        </div>
        <div className="store-dash-card">
          <span>Created (30d)</span>
          <strong>{createdLast30Days.toLocaleString()}</strong>
        </div>
        <div className="store-dash-card">
          <span>Finished (30d)</span>
          <strong>{completedLast30Days.toLocaleString()}</strong>
        </div>
      </div>

      {cycle && (
        <section className="store-dash-chart store-dash-cycle">
          <div className="store-dash-chart-head">
            <h2>{cycle.name}</h2>
            <em>{Math.round(cycle.progress * 100)}%</em>
          </div>
          <div className="store-dash-progress">
            <i style={{ width: `${Math.max(0, Math.min(100, cycle.progress * 100))}%` }} />
          </div>
          {(cycle.startsAt || cycle.endsAt) && (
            <small>
              {[formatDate(cycle.startsAt), formatDate(cycle.endsAt)].filter(Boolean).join(' – ')}
            </small>
          )}
        </section>
      )}

      <div className="store-dash-fleet-grid">
        <section className="store-dash-chart">
          <h2>{title} issue progress</h2>
          {total === 0 ? (
            <p className="store-dash-hint">No issues in this view yet.</p>
          ) : (
            <div className="store-dash-fleet">
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={slices}
                    dataKey="value"
                    nameKey="displayName"
                    cx="50%"
                    cy="50%"
                    outerRadius={108}
                    labelLine={false}
                    label={renderLabel}
                  >
                    {slices.map((slice) => (
                      <Cell key={slice.name} fill={slice.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value: number, name: string) => [
                      `${Number(value).toLocaleString()} issues${total ? ` (${((Number(value) / total) * 100).toFixed(1)}%)` : ''}`,
                      name,
                    ]}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="store-dash-legend">
                {slices.map((slice) => (
                  <div key={slice.name} className="store-dash-legend-item">
                    <i style={{ background: slice.color }} />
                    <span>{slice.displayName}</span>
                    <em>{slice.value.toLocaleString()}</em>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>

        <section className="store-dash-chart">
          <h2>Projects</h2>
          {projects.length === 0 ? (
            <p className="store-dash-hint">No planned or started projects.</p>
          ) : (
            <div className="store-dash-legend">
              {projects.map((project) => (
                <div key={project.name} className="store-dash-project">
                  <div className="store-dash-legend-item">
                    <span>{project.name}</span>
                    <em>{Math.round(project.progress * 100)}%</em>
                  </div>
                  <div className="store-dash-progress">
                    <i style={{ width: `${Math.max(0, Math.min(100, project.progress * 100))}%` }} />
                  </div>
                  {project.targetDate && <small>Target {formatDate(project.targetDate)}</small>}
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      <div className="store-dash-fleet-grid">
        <section className="store-dash-chart">
          <h2>Workflow states</h2>
          <CountList rows={states} />
        </section>
        <section className="store-dash-chart">
          <h2>Priority</h2>
          <CountList rows={priorities} />
        </section>
      </div>
    </>
  );
};

const StoreDashboardWorkView: React.FC = () => {
  const [data, setData] = useState<WorkData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTeamId, setSelectedTeamId] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/store-dashboard/work`, { withCredentials: true });
      setData(response.data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load work');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const selectedTeam = useMemo(
    () => data?.teams.find((team) => team.id === selectedTeamId) || null,
    [data, selectedTeamId],
  );

  return (
    <div className="store-dash">
      <div className="store-dash-toolbar">
        <div>
          <div className="store-dash-kicker">{data?.organization ? `Linear · ${data.organization}` : 'Linear'}</div>
          <h1>Work</h1>
        </div>
      </div>

      {data && !data.configured && (
        <p className="store-dash-hint">
          Connect Linear on the <Link to="/apis">APIs</Link> page to load issue progress.
        </p>
      )}

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : error || data?.error ? (
        <div className="alert alert-danger">{error || data?.error}</div>
      ) : (
        <>
          <section className="store-dash-chart">
            <h2>Teams</h2>
            {(data?.teams || []).length === 0 ? (
              <p className="store-dash-hint">No teams in this workspace yet.</p>
            ) : (
              <div className="store-dash-team-list">
                {data?.teams.map((team) => (
                  <button
                    key={team.id}
                    type="button"
                    className={`store-dash-team-row${selectedTeamId === team.id ? ' is-active' : ''}`}
                    onClick={() => setSelectedTeamId(team.id)}
                  >
                    <strong>
                      {team.name}
                      {team.cycle && (
                        <small>
                          {team.cycle.name}
                          {' · '}
                          {Math.round(team.cycle.progress * 100)}%
                        </small>
                      )}
                    </strong>
                    <span>Open <em>{team.open.toLocaleString()}</em></span>
                    <span>In progress <em>{team.status.started.toLocaleString()}</em></span>
                    <span>Done <em>{team.status.completed.toLocaleString()}</em></span>
                    <span>Finished 30d <em>{team.completedLast30Days.toLocaleString()}</em></span>
                  </button>
                ))}
              </div>
            )}
          </section>

          <div className="store-dash-page-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={selectedTeamId === 'all'}
              className={`store-dash-page-tab${selectedTeamId === 'all' ? ' is-active' : ''}`}
              onClick={() => setSelectedTeamId('all')}
            >
              All teams
            </button>
            {(data?.teams || []).map((team) => (
              <button
                key={team.id}
                type="button"
                role="tab"
                aria-selected={selectedTeamId === team.id}
                className={`store-dash-page-tab${selectedTeamId === team.id ? ' is-active' : ''}`}
                onClick={() => setSelectedTeamId(team.id)}
              >
                {team.name}
              </button>
            ))}
          </div>

          {selectedTeam ? (
            <TeamDetail
              title={selectedTeam.name}
              status={selectedTeam.status}
              open={selectedTeam.open}
              completedLast30Days={selectedTeam.completedLast30Days}
              createdLast30Days={selectedTeam.createdLast30Days}
              states={selectedTeam.states}
              priorities={selectedTeam.priorities}
              projects={selectedTeam.projects}
              cycle={selectedTeam.cycle}
            />
          ) : (
            <TeamDetail
              title="Workspace"
              status={data?.status || emptyStatus()}
              open={data?.open || 0}
              completedLast30Days={data?.completedLast30Days || 0}
              createdLast30Days={data?.createdLast30Days || 0}
              states={data?.states || []}
              priorities={data?.priorities || []}
              projects={data?.projects || []}
            />
          )}
        </>
      )}
    </div>
  );
};

function emptyStatus(): WorkStatus {
  return { backlog: 0, started: 0, completed: 0, canceled: 0 };
}

export default StoreDashboardWorkView;
